import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';
import urlParamsService from '../utils/urlParams';

const ApplicationFormFields = ({ 
  formData, 
  onInputChange, 
  onSubmit, 
  submitButtonText = "Apply Now",
  showPrivacyNote = true,
  className = "",
  onSubmissionSuccess = null,
  onSubmissionError = null
}) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [specializations, setSpecializations] = useState([]);
  const [specializationLoading, setSpecializationLoading] = useState(false);
  const [specializationError, setSpecializationError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [partnerId, setPartnerId] = useState(null);

  // Initialize partner_id from URL parameter on component mount
  useEffect(() => {
    const currentPartnerId = urlParamsService.getPartnerId();
    setPartnerId(currentPartnerId);

    // Listen for partner_id changes when URL ref parameter changes
    const handlePartnerIdChange = (newPartnerId) => {
      setPartnerId(newPartnerId);
    };

    urlParamsService.addPartnerIdChangeListener(handlePartnerIdChange);

    // Cleanup listener on component unmount
    return () => {
      urlParamsService.removePartnerIdChangeListener(handlePartnerIdChange);
    };
  }, []);

  // Fetch courses from API with caching to prevent duplicate calls
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        // Check if courses are already cached in memory
        if (window.cachedCourses) {
          setCourses(window.cachedCourses);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError('');
        
        const response = await apiService.getUniversityCourses();

        if (response.success && response.data && response.data.data && response.data.data.courses) {
          const coursesData = response.data.data.courses;
          setCourses(coursesData);
          // Cache courses in memory to prevent duplicate API calls
          window.cachedCourses = coursesData;
        } else if (response.success && response.data && response.data.courses) {
          const coursesData = response.data.courses;
          setCourses(coursesData);
          // Cache courses in memory to prevent duplicate API calls
          window.cachedCourses = coursesData;
        } else {
          setError('Failed to load courses');
        }
      } catch (error) {
        setError('Error loading courses');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  // Helper function to get course ID from course value
  const getCourseIdFromValue = useCallback((courseValue) => {
    // If courseValue is already a numeric ID, return it
    if (!isNaN(courseValue)) {
      return courseValue;
    }
    
    // Map course values to actual course IDs based on course pages
    if (courses.length > 0) {
      // For B.Tech courses (Bachelor of Technology)
      if (courseValue.toLowerCase().includes('b-tech') || courseValue.toLowerCase().includes('btech')) {
        const btechCourse = courses.find(course => course.short_code === 'BTECH' || course.name === 'Bachelor of Technology');
        return btechCourse ? btechCourse.id : 1; // Default to BTECH (id: 1)
      }
      
      // For M.Tech courses (Master of Technology)
      if (courseValue.toLowerCase().includes('m-tech') || courseValue.toLowerCase().includes('mtech')) {
        const mtechCourse = courses.find(course => course.short_code === 'MTECH' || course.name === 'Master of Technology');
        return mtechCourse ? mtechCourse.id : 2; // Default to MTECH (id: 2)
      }
      
      // Try direct match for other formats
      const matchingCourse = courses.find(course => 
        course.name === courseValue || 
        course.short_code === courseValue ||
        course.name.toLowerCase().replace(/\s+/g, '-') === courseValue.toLowerCase()
      );
      
      return matchingCourse ? matchingCourse.id : null;
    }
    
    return null;
  }, [courses]);

  // Shared function to fetch specializations
  const handleSpecializationFetch = useCallback(async (courseValue) => {
    if (!courseValue || courseValue === '') return;
    
    // Convert course value to ID if needed
    const courseId = getCourseIdFromValue(courseValue);
    if (!courseId) {
      setSpecializationError('Invalid course selection');
      return;
    }
    
    try {
      setSpecializationLoading(true);
      
      const response = await apiService.getCourseSpecializations(courseId);
      
      // Handle nested response structure: response.data.data.data
      let specializationsData = [];
      
      if (response.success && response.data) {
        // Check for nested data structure
        if (response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          specializationsData = response.data.data.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          specializationsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          specializationsData = response.data;
        }
      }
      
      if (specializationsData.length > 0) {
        setSpecializations(specializationsData);
        setSpecializationError(''); // Clear any previous errors
      } else {
        setSpecializationError('Failed to load specializations');
      }
    } catch (error) {
      setSpecializationError('Error loading specializations');
    } finally {
      setSpecializationLoading(false);
    }
  }, [getCourseIdFromValue]);

  // Fetch specializations when component mounts - runs once after courses are loaded
  useEffect(() => {
    // Wait for courses to load first, then check if we have a default course
    if (courses.length > 0 && formData.course && formData.course !== '' && specializations.length === 0) {
      handleSpecializationFetch(formData.course);
    }
  }, [courses, formData.course, specializations.length, handleSpecializationFetch]);

  // Set default specialization when specializations load for default course
  useEffect(() => {
    if (specializations.length > 0 && formData.course && formData.course !== '' && !formData.specialization) {
      // Auto-select first specialization if none selected
      const firstSpecialization = specializations[0];
      if (firstSpecialization) {
        onInputChange({
          target: {
            name: 'specialization',
            value: firstSpecialization.id
          }
        });
      }
    }
  }, [specializations, formData.course, formData.specialization, onInputChange]);

  // Fetch specializations when course changes
  const handleCourseChange = async (e) => {
    const courseId = e.target.value;
    
    // Reset specialization
    setSpecializations([]);
    setSpecializationError('');
    
    // Call parent handler
    onInputChange(e);
    
    // Fetch specializations for a new course
    await handleSpecializationFetch(courseId);
  };

  // Reset form to initial state
  const resetForm = () => {
    // Reset form data to initial state
    const initialFormData = {
      name: '',
      email: '',
      phone: '',
      course: '',
      specialization: '',
      employment_status: ''
    };
    
    // Update form data through parent handler
    Object.keys(initialFormData).forEach(key => {
      onInputChange({
        target: {
          name: key,
          value: initialFormData[key]
        }
      });
    });
    
    // Reset specializations
    setSpecializations([]);
    setSpecializationError('');
    setSubmissionError('');
  };

  // Handle form submission with API
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous submission errors
    setSubmissionError('');
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone || !formData.course || !formData.specialization || !formData.employment_status) {
      setSubmissionError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      
      // Prepare lead data according to API specification
      const leadData = {
        partner_id: partnerId || 1, // Use partner_id from URL ref parameter or default to 1
        full_name: formData.name,
        email: formData.email,
        mobile: formData.phone,
        course_id: parseInt(formData.course),
        specialization_id: parseInt(formData.specialization),
        employment_status: formData.employment_status
      };

      // Submit lead via API
      const response = await apiService.submitLead(leadData);
      if (response.success && response.data) {
        // Success - call parent success handler if provided
        if (onSubmissionSuccess) {
          onSubmissionSuccess(response.data);
        } else {
          // Default success behavior
          alert('Application submitted successfully!');
        }
        
        // Reset form after successful submission
        resetForm();
        setTimeout(() => {  
          window.location.href = '/thankyou.html';
        }, 500);
        
        // Don't call parent onSubmit handler to prevent duplicate alerts
        // The API submission is handled here, no need for parent handler
      } else {
        // API returned error
        const errorMessage = response.data?.message || 'Failed to submit application';
        setSubmissionError(errorMessage);
        
        if (onSubmissionError) {
          onSubmissionError(errorMessage);
        }
      }
    } catch (error) {
      // Network or other error
      const errorMessage = apiService.handleApiError(error);
      setSubmissionError(errorMessage);
      
      if (onSubmissionError) {
        onSubmissionError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleFormSubmit} className={className} autoComplete="off">
      <div className="mb-3">
        <input
          type="text"
          name="name"
          className="form-control application-form-input"
          id="name"
          placeholder="Your Name"
          value={formData.name}
          onChange={onInputChange}
          required
        />
      </div>

      <div className="mb-3">
        <input
          type="email"
          className="form-control application-form-input"
          name="email"
          id="email"
          placeholder="Your Email"
          value={formData.email}
          onChange={onInputChange}
          required
        />
      </div>

      <div className="mb-3">
        <input
          type="tel"
          className="form-control application-form-input"
          name="phone"
          id="phone"
          placeholder="Your Phone"
          value={formData.phone}
          onChange={onInputChange}
          required
        />
      </div>

      <div className="mb-3">
        <select
          className="form-select application-form-input"
          name="course"
          id="course"
          value={formData.course}
          onChange={handleCourseChange}
          required
          disabled={loading}
        >
          <option value="" disabled>
            {loading ? 'Loading courses...' : 'Select Course'}
          </option>
          {error ? (
            <option value="" disabled>Error loading courses</option>
          ) : (
            courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.name} ({course.short_code})
              </option>
            ))
          )}
        </select>
        {error && (
          <div className="text-danger small mt-1">
            {error}
          </div>
        )}
      </div>

      <div className="mb-3">
        <select
          className="form-select application-form-input"
          name="specialization"
          id="specialization"
          value={formData.specialization}
          onChange={onInputChange}
          required
          disabled={specializationLoading || !formData.course || formData.course === ''}
        >
          <option value="" disabled>
            {!formData.course || formData.course === '' 
              ? 'Select Course First' 
              : specializationLoading 
                ? 'Loading specializations...' 
                : 'Select Branch/Specialization'
            }
          </option>
          {specializationError ? (
            <option value="" disabled>Error loading specializations</option>
          ) : (
            Array.isArray(specializations) && specializations.length > 0 ? (
              specializations.map((specialization) => (
                <option key={specialization.id} value={specialization.id}>
                  {specialization.name}
                </option>
              ))
            ) : (
              <option value="" disabled>No specializations available</option>
            )
          )}
        </select>
        {specializationError && (
          <div className="text-danger small mt-1">
            {specializationError}
          </div>
        )}
      </div>

      <div className="mb-4">
        <select
          className="form-select application-form-input"
          name="employment_status"
          id="employment_status"
          value={formData.employment_status}
          onChange={onInputChange}
        >
          <option value="" disabled>Select Employment Status</option>
          <option value="employed">Employed</option>
          <option value="unemployed">Unemployed</option>
          <option value="student">Student</option>
          <option value="freelancer">Freelancer</option>
          <option value="government_job">Government Job</option>
          <option value="private_job">Private Job</option>
          <option value="retired">Retired</option>
          <option value="other">Other</option>
        </select>
      </div>

      {submissionError && (
        <div className="alert alert-danger mb-3" role="alert">
          <small>{submissionError}</small>
        </div>
      )}

      {showPrivacyNote && (
        <div className="text-center mb-3">
          <p className="text-dark small mb-0">
            Your personal information is secure with us.
          </p>
        </div>
      )}

      <button 
        type="submit" 
        className="btn btn-danger w-100 py-3 fw-bold text-uppercase rounded-pill border border-dark"
        disabled={submitting || loading || specializationLoading}
      >
        {submitting ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Submitting...
          </>
        ) : (
          submitButtonText
        )}
      </button>
    </form>
  );
};

export default ApplicationFormFields;
